import type { AlertColor } from "@mui/material/Alert/Alert";
import Box from "@mui/material/Box";
import { useEffect, useMemo, useState } from "react";

import useQueryClient from "../../../api/query";
import type { BackendFilters } from "../../../utils/backendFilters";
import { camelToSnakeCase } from "../../../utils/utils";
import DataTable, {
  type PaginationModel,
  type SortModel,
  useServerFilterModel,
} from "../../DataTable";
import { MASK } from "../../redact";
import CustomNoRowsOverlay from "../../TableNoDataOverlay";
import { CredentialsDeleteDialog } from "../SettingsDialog/CredentialsDeleteDialog";
import { OpsCredentialsDialog } from "../SettingsDialog/OpsCredentialsDialog";
import { getOpsColumns, type OpsRow } from "./OpsCredentialsTableColumns";

interface OpsCredentialsTableProps {
  credentials: OpsCredentialsMessage[];
  practices: PracticeMessage[];
  loading: boolean;
  pagination: { page: number; pageSize: number; rowCount: number };
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onSortChange: (field: string | null, direction: "asc" | "desc") => void;
  onFilterChange: (filters: BackendFilters) => void;
  onRefresh: () => void;
  updateSnackBar: (snackbar: { severity: AlertColor; message: string }) => void;
}

export function OpsCredentialsTable({
  credentials,
  practices,
  loading,
  pagination,
  onPageChange,
  onPageSizeChange,
  onSortChange,
  onFilterChange,
  onRefresh,
  updateSnackBar,
}: OpsCredentialsTableProps) {
  const queryClient = useQueryClient();
  const [editCredential, setEditCredential] =
    useState<OpsCredentialsMessage | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState<
    | (Partial<UserCredentialsMessage> & { removeUserCredential: () => void })
    | null
  >(null);
  const [targetOptions, setTargetOptions] = useState<string[]>([]);
  const { filterModel, onFilterModelChange } = useServerFilterModel({
    mapField: camelToSnakeCase,
    onFiltersChange: onFilterChange,
  });
  const [sortModel, setSortModel] = useState<SortModel>([
    { field: "updatedAt", sort: "desc" },
  ]);

  useEffect(() => {
    let cancelled = false;
    queryClient
      .getOpsTargetOptions()
      .then((options) => {
        if (!cancelled) {
          setTargetOptions(options);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [queryClient]);

  const rows = useMemo<OpsRow[]>(
    () =>
      credentials.map((credential) => ({
        ...credential,
        unmaskValue: async (name: string): Promise<string> => {
          try {
            const response = await queryClient.unmaskOpsAttribute(
              credential.wieldyId,
              name,
            );
            return response[name] ?? MASK;
          } catch {
            return MASK;
          }
        },
        fetchTotp: () => queryClient.getOpsCredentialTotp(credential.wieldyId),
      })),
    [credentials, queryClient],
  );

  // The endpoint sorts by one field, so only the primary key is sent.
  const handleSortModelChange = (model: SortModel) => {
    setSortModel(model);
    if (model.length > 0) {
      onSortChange(model[0].field, model[0].sort);
    } else {
      onSortChange(null, "desc");
    }
  };

  const handlePaginationModelChange = (model: PaginationModel) => {
    if (model.pageSize !== pagination.pageSize) {
      onPageSizeChange(model.pageSize);
    } else if (model.page + 1 !== pagination.page) {
      onPageChange(model.page + 1);
    }
  };

  const openEdit = (row: OpsRow) => {
    setEditCredential(row);
    setEditOpen(true);
  };
  const closeEdit = () => {
    setEditOpen(false);
    setEditCredential(null);
  };

  const openDelete = (row: OpsRow) => {
    setDeleteRow({
      wieldyId: row.wieldyId,
      name: row.name,
      removeUserCredential: onRefresh,
    });
  };

  return (
    <>
      <Box sx={{ padding: "1rem 0" }}>
        <DataTable
          columns={getOpsColumns(openEdit, openDelete, targetOptions)}
          rows={rows}
          getRowId={(row) => row.wieldyId}
          loading={loading}
          rowCount={pagination.rowCount}
          paginationModel={{
            page: pagination.page - 1,
            pageSize: pagination.pageSize,
          }}
          onPaginationModelChange={handlePaginationModelChange}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          filterModel={filterModel}
          onFilterModelChange={onFilterModelChange}
          noRowsOverlay={
            <CustomNoRowsOverlay
              title="No credentials"
              description="No ops credentials match the current filters."
            />
          }
        />
      </Box>
      <OpsCredentialsDialog
        open={editOpen}
        onClose={closeEdit}
        practices={practices}
        credential={editCredential}
        afterSubmit={onRefresh}
        updateSnackBar={updateSnackBar}
      />
      {deleteRow && (
        <CredentialsDeleteDialog
          credentials={deleteRow}
          updateSnackBar={updateSnackBar}
          open={!!deleteRow}
          onClose={() => setDeleteRow(null)}
          deleteHandler={(credentialId) =>
            queryClient.deleteOpsCredential(credentialId)
          }
        />
      )}
    </>
  );
}
